import { type Prisma, UserLocale } from "@prisma/client";
import { type AsyncReturnType } from "type-fest";
import { number, object, string } from "yup";
import { type AppContext } from "~/lib/context";
import { CacheKeys } from "~/lib/external/redis/cacheKeys";
import { omit } from "~/lib/lodash";
import { type YupOutputType } from "~/lib/utils";

export const selectComptryxLevelForSelector = {
  id: true,
  name: true,
  description: true,
  meta: true,
} satisfies Prisma.BenchmarkLevelSelect;

export const fetchComptryxLevels = async (ctx: AppContext) => {
  return fetchCached(ctx, async () => {
    const framework = await ctx.prisma.levelFramework.findFirstOrThrow({ where: { name: "Comptryx" } });

    const levels = await ctx.prisma.benchmarkLevel.findMany({
      where: { frameworkId: framework.id },
      select: selectComptryxLevelForSelector,
    });

    return processComptryxLevels(levels);
  });
};

export const fetchComptryxLevelsByIds = async (ctx: AppContext, ids: number[]) => {
  const levels = await ctx.prisma.benchmarkLevel.findMany({
    where: { id: { in: ids } },
    select: selectComptryxLevelForSelector,
  });

  return processComptryxLevels(levels);
};

const fetchCached = async <T>(ctx: AppContext, fn: () => Promise<T>) => {
  const locale = ctx.user?.locale ?? UserLocale.EN;
  const cacheKey = `${CacheKeys.ComptryxLevels}-${locale}`;

  return ctx.remember(cacheKey, fn);
};

const processComptryxLevels = (
  levels: Prisma.BenchmarkLevelGetPayload<{ select: typeof selectComptryxLevelForSelector }>[]
) => {
  return levels
    .map((level) => processComptryxLevel(level))
    .sort((levelA, levelB) => {
      return levelB.position - levelA.position;
    });
};

const ComptryxLevelMetaSchema = object({
  code: string().required(),
  familyName: string().required(),
  categoryName: string().required(),
  categoryCode: string().required(),
  position: number().required(),
});

type FetchLevelResult = Prisma.BenchmarkLevelGetPayload<{ select: typeof selectComptryxLevelForSelector }>;

export const processComptryxLevel = <
  T extends FetchLevelResult | null,
  R = T extends null ? undefined : Omit<T, "meta"> & YupOutputType<typeof ComptryxLevelMetaSchema>,
>(
  level: T
): R => {
  if (!level) return undefined as R;

  const meta = ComptryxLevelMetaSchema.validateSync(level.meta);

  return {
    ...omit(level, "meta"),
    ...meta,
  } as R;
};

export type FetchComptryxLevelsResult = AsyncReturnType<typeof fetchComptryxLevels>;

export type ComptryxLevelForSelector = FetchComptryxLevelsResult[number];
